/**
 * useOrgInfo — fetch + memoize the current user's org info.
 *
 * Why a hook + module-level cache (not React Query, not Context):
 *   - The data rarely changes (org name, role, team-mode bit).
 *   - Multiple components want it (Workbench banner, settings panels).
 *   - Adding a context provider for one small payload is overkill.
 *
 * The cache is a single in-flight promise, so concurrent callers share
 * the same network request. Refresh by calling ``refresh()`` (e.g. after
 * accepting an invitation or removing a member, where ``is_team_mode``
 * may flip). The backend already caches for 30s, so ``refresh()`` is
 * cheap to call optimistically.
 */
import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/apiFetch'

export interface OrgInfo {
  id: string
  name: string
  role: 'admin' | 'member' | string
  /** True when the org has ≥2 active members. Gates presence UI in the
   *  editor — solo users (single-member orgs, or no org at all) don't
   *  see "X is editing" banners or "Last edited by" attribution. */
  is_team_mode: boolean
}

interface OrgInfoResult {
  org: OrgInfo | null
  /** True when the backend answered 402: a valid login with no subscription
   *  (pay-first dead-end). The app shell must not render — `SubscriptionGate`
   *  in App.tsx shows the subscribe screen instead. Distinct from `org: null`
   *  alone, which also covers transient errors and dev fallback mode. */
  subscriptionRequired: boolean
}

let cached: Promise<OrgInfoResult> | null = null

async function fetchOrgInfo(): Promise<OrgInfoResult> {
  try {
    const res = await apiFetch('/api/org')
    if (res.status === 402) return { org: null, subscriptionRequired: true }
    if (!res.ok) return { org: null, subscriptionRequired: false }
    return { org: (await res.json()) as OrgInfo, subscriptionRequired: false }
  } catch {
    return { org: null, subscriptionRequired: false }
  }
}

function getOrgInfo(): Promise<OrgInfoResult> {
  if (!cached) cached = fetchOrgInfo()
  return cached
}

/** Force a re-fetch on next ``useOrgInfo`` call. Use after member-list
 *  changes (invite accepted, member removed) where ``is_team_mode`` can
 *  flip. */
export function invalidateOrgInfo(): void {
  cached = null
}

export function useOrgInfo(): {
  org: OrgInfo | null
  loading: boolean
  subscriptionRequired: boolean
} {
  const [org, setOrg] = useState<OrgInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [subscriptionRequired, setSubscriptionRequired] = useState(false)

  useEffect(() => {
    let cancelled = false
    getOrgInfo().then((result) => {
      if (cancelled) return
      setOrg(result.org)
      setSubscriptionRequired(result.subscriptionRequired)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return { org, loading, subscriptionRequired }
}
